import {Injectable} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';

import {CreditCard} from '../models/credit-card.model';
import {PaymentMethod} from '../models/payment-method.model';
import {CostCenter} from '../models/cost-center.model';
import {Order} from '../models/order.model';
import {AuthService} from './auth.service';
import {OrderItem} from '../models/order-item.model';
import {OrderItemDelivery} from '../models/order-item-delivery.model';
import {TransactionGroup} from '../models/transaction-group.model';
import {Address} from '../models/address.model';
import {IDecisionPointData} from '../interfaces/decision-point.data';
import {IPaymentJsToken} from '../interfaces/payment-js.token';
import {Observable, from, of, firstValueFrom} from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {MobileCheckServiceService} from './mobile-check-service.service';
import {ConfigService} from './config.service';
import {DistributionList} from '../models/distribution-list.model';
import {ChiliProjectData, ChiliSelectResult} from '../interfaces/chili-grafix';
import {DistributionGroup} from '../interfaces/distribution-group';
import {EditContactDetailsResult} from '../interfaces/edit-contact-details-result';
import {Store} from '@ngrx/store';
import {PnpBudgetService} from './pnp-budget.service';
import {selectHasUserBudgets} from '../store/selectors/pnp-budget.selectors';
import {IAppState} from '../store/app.store';

export interface TermsResult  {accept?: boolean, logout?: boolean}

@Injectable()
export class ModalService {

    /**
     * All modal dialog components live in a separate lazy chunk. Importing the module
     * here (eagerly, on service construction) kicks off the download in parallel with
     * main.js, so by the time a user triggers a modal the chunk is already in memory.
     * Each show*() method awaits this promise (resolves instantly once loaded) and
     * destructures the component class it needs.
     */
    private readonly _dialogs = import('../theme/theme-dialogs.module');

    constructor(private ngbModal: NgbModal, private authService: AuthService,
                private mobileCheck: MobileCheckServiceService,
                private configService: ConfigService,
                private pnpBudgetService: PnpBudgetService,
                private store: Store<IAppState>) {}

    public async showCreditCardDialog(orderId: number = null,
                                creditCard: CreditCard = null,
                                token: IPaymentJsToken, save = false): Promise<CreditCard> {
        const { CreditCardModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(CreditCardModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.orderId = orderId;
        modal.componentInstance.card = creditCard;
        modal.componentInstance.token = token;
        modal.componentInstance.saveToPaymentMethods = save;
        return modal.result;
    }

    public async showCreditCardLabelDialog(creditCard: CreditCard): Promise<CreditCard> {
        if (!creditCard) {
            return;
        }
        const { CreditCardLabelModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(CreditCardLabelModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container'
            , windowClass: this.modalWindowClass});
        modal.componentInstance.creditCard = creditCard;
        return modal.result;
    }

    public async showRemoveItemConfirmation(paymentMethod: PaymentMethod) {
        if (!paymentMethod) {
            return;
        }
        const { RemovePaymentMethodModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(RemovePaymentMethodModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: `${this.modalWindowClass} in-center`});
        modal.componentInstance.item = paymentMethod;
        return modal.result;
    }

    public async showSapAccountDialog(costCenter: CostCenter = null, isAdd: boolean = false): Promise<CostCenter> {
        const { SapAccountComponent } = await this._dialogs;
        const modal = this.ngbModal.open(SapAccountComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});

        modal.componentInstance.costCenter = costCenter || new CostCenter({});
        modal.componentInstance.operationType = isAdd ? 'Add' : 'Edit';
        return modal.result;
    }

    public async showSAPModal(order: Order, canChangeOrderType = true, closeWithWarning = false, headerLabel  = '',
                        shouldUpdateShipments = false, orderType: string = ''): Promise<Order> {
        const { SapModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(SapModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass, keyboard: true});
        modal.componentInstance.orderId = order?.id;
        modal.componentInstance.orderType = orderType; // in case order is not created  yet
        modal.componentInstance.headerLabel = headerLabel;
        modal.componentInstance.canChangeOrderType = canChangeOrderType;
        modal.componentInstance.closeWithWarning = closeWithWarning;
        modal.componentInstance.shouldUpdateShipments = shouldUpdateShipments;
        return modal.result.then(result => {
            return result
        }).catch( () => {
            return null;
        });
    }

    public async showOrderSettingsModal(order: Order, shouldUpdateShipments = false, orderType: string  = ''): Promise<Order> {
        const { OrderSettingsModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(OrderSettingsModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass, keyboard: true});
        modal.componentInstance.order = order;
        modal.componentInstance.orderType = orderType; // in case order is not created  yet
        modal.componentInstance.shouldUpdateShipments = shouldUpdateShipments;
        return modal.result.then(result => {
            if (result === 'openSapDialog') {
                return this.showSAPModal(order);
            }
            return result;
        }).catch(() => {
                return null;
            }
        );
    }

    public editOrderSettings(order: Order, options: {headerLabel?: string, shouldUpdateShipments?: boolean}
        = {headerLabel: '', shouldUpdateShipments: false}): Promise<Order> {
        if (!order) {
            return new Promise<Order>(resolve => resolve(null));
        }
        if (this.authService.canShowSAP && (this.configService.showIOAndWBS || this.configService.showBillingInfo)) {
            return this.showSAPModal(order, true, false, options.headerLabel, options.shouldUpdateShipments);
        } else {
            return this.showOrderSettingsModal(order, options.shouldUpdateShipments);
        }
    }

    public createOrderWithSettings(options: {headerLabel?: string, shouldUpdateShipments?: boolean, orderType?: string}
                                   = {headerLabel: '', shouldUpdateShipments: false, orderType: null}): Observable<Order> {
        if (this.configService.showIOAndWBS || this.configService.showBillingInfo) {
            return from(this.showSAPModal(null, true, false, options.headerLabel, options.shouldUpdateShipments, options.orderType));
        } else {
            return from(this.showOrderSettingsModal(null, options.shouldUpdateShipments, options.orderType));
        }
    }


    public async editOrderItemModal(order: Order, orderItem: OrderItem, checkForChanges?: (orderItem: OrderItem) => boolean | Promise<boolean>,
                              showOrderSelector = false): Promise<OrderItem | null> {
        const { OrderItemModalComponent } = await this._dialogs;
        const beforeDismiss = () => {
            if (!modal.componentInstance.updateResult) {
                return true;
            }
            return checkForChanges ? checkForChanges(modal.componentInstance.orderItem) : true;
        }
        const modal = this.ngbModal.open(OrderItemModalComponent,
            {keyboard: true, backdrop: true, size: 'xl', beforeDismiss,
                backdropClass: 'backdrop--container',
                windowClass: 'modal-window--container'});

        const instance = modal.componentInstance;
        instance.order = order;
        instance.orderItem = orderItem;
        instance.showOrderSelector = showOrderSelector;
        return modal.result.catch( () => {
            return instance.updateResult ? instance.orderItem : null;
        });
    }

    public async showReadonlyProductModal(order: Order, orderItem: OrderItem): Promise<OrderItem> {
        const { OrderItemModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(OrderItemModalComponent,
            {keyboard: true, backdrop: true, size: 'xl',
                backdropClass: 'backdrop--container',
                windowClass: this.modalWindowClass});
        modal.componentInstance.order = order;
        modal.componentInstance.orderItem = orderItem;
        modal.componentInstance.readonly = true;
        return modal.result.catch( () => {
            return null;
        });
    }

    public async showAlert(settings: Record<string, unknown> = null, size = 'sm'): Promise<boolean> {
        const { AlertComponent } = await this._dialogs;
        const modal = this.ngbModal.open(AlertComponent, {backdrop: 'static', backdropClass: 'backdrop--container', size, keyboard: false});
        if (settings) {
            modal.componentInstance.settings = settings;
        }
        return modal.result;
    }


    public showProductCustomizationModal(data: ChiliProjectData | null, readonly = false): Observable<OrderItemDelivery> {
      if (!data) {
        return of(undefined);
      }
      return from(this._dialogs.then(({ CustomizationDialogComponent }) => {
        const modal = this.ngbModal.open(CustomizationDialogComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            size: 'xl', windowClass: this.modalWindowClass});
        modal.componentInstance.chiliData = data;
        modal.componentInstance.readonly = readonly;
        return modal.result;
      }));
    }

    public showCustomizationSelection(orderItem: OrderItem): Observable<string | ChiliSelectResult | undefined> {
      if (!orderItem) {
        return of(undefined);
      }
      return from(this._dialogs.then(({ SelectCustomizationDialogComponent }) => {
        const modal = this.ngbModal.open(SelectCustomizationDialogComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
          size: 'md', windowClass: this.modalWindowClass});
        modal.componentInstance.orderItem = orderItem;
        return modal.result;
      }));
    }

    public async showSelectSAPModal(costCenter: CostCenter = null) {
        const { SelectSapComponent } = await this._dialogs;
        const modal = this.ngbModal.open(SelectSapComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.costCenter = costCenter;
        return modal.result;
    }

    public async showPriceDetails( order: Order, orderItem: OrderItem = null) {

        const showBudgetDetails = () => {
            this.showBudgetDetails([orderItem]);
        }

        const { PriceDetailsModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(PriceDetailsModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            size: 'sm'});
        modal.componentInstance.order = order;
        modal.componentInstance.orderItem = orderItem;
        modal.componentInstance.showBudgetDetails = showBudgetDetails;
    }

    public async showShipToModal(order: Order, orderItem: OrderItem,
                           orderItemDelivery: OrderItemDelivery,
                           waitForUpdate = true): Promise<{attention?: string}>  {
        const { ShipToModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(ShipToModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            size: 'sm', windowClass: this.modalWindowClass});
        modal.componentInstance.order = order;
        modal.componentInstance.orderItem = orderItem;
        modal.componentInstance.orderItemDelivery = orderItemDelivery;
        modal.componentInstance.waitForUpdate = waitForUpdate;

        return modal.result;
    }

    public async showInternalOrderModal(order: Order, orderItem: OrderItem,
                           orderItemDelivery: OrderItemDelivery,
                           waitForUpdate = true): Promise<{internalOrder?: string}>  {
        const { InternalOrderModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(InternalOrderModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            size: 'sm', windowClass: this.modalWindowClass});
        modal.componentInstance.order = order;
        modal.componentInstance.orderItem = orderItem;
        modal.componentInstance.orderItemDelivery = orderItemDelivery;
        modal.componentInstance.waitForUpdate = waitForUpdate;

        return modal.result;
    }


    public async showTransactionDetailsModal(transactionGroup: TransactionGroup): Promise<TransactionGroup> {
        const { TransactionDetailsModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(TransactionDetailsModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            size: 'lg', windowClass: this.modalWindowClass});
        modal.componentInstance.transactionGroup = transactionGroup;
        return modal.result;
    }

    public async showTransactionResult(transactionGroup: TransactionGroup): Promise<string> {
        const { TransactionResultModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(TransactionResultModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.transactionGroup = transactionGroup;
        return modal.result;
    }

    public async showCsvUploadResult(uploadResult: Record<string, unknown> = {}): Promise<unknown> {
        const { CsvUploadResultComponent } = await this._dialogs;
        const modal = this.ngbModal.open(CsvUploadResultComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.uploadResult = uploadResult;
        return modal.result;
    }

    public async showRemoveOrderModal(order: Order): Promise<boolean> {
        const { RemoveOrderModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(RemoveOrderModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.order = order;
        return modal.result;
    }

    public async showRemoveOrderItemModal(orderItem: OrderItem): Promise<OrderItem> {
        const { RemoveOrderItemModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(RemoveOrderItemModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.orderItem = orderItem;
        return modal.result;
    }

    public async showRemoveItemModal(options: {label?: string, closeButton?: string, removeButton?: string} = {}): Promise<boolean> {
        const { RemoveItemModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(RemoveItemModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.options = options;
        return modal.result;
    }

    public async editAddress(address: Address, isDeliveryAddress = false): Promise<Address> {
        const { AddressModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(AddressModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.selectedAddress = address;
        modal.componentInstance.isDeliveryAddress = isDeliveryAddress;
        return modal.result;
    }

    public editDistributionList(distributionList?: DistributionList | undefined): Observable<DistributionList | undefined> {
        return from(this._dialogs.then(({ DistributionListModalComponent }) => {
            const modal = this.ngbModal.open(DistributionListModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
                windowClass: this.modalWindowClass});
            modal.componentInstance.distributionList = distributionList;
            return modal.result;
        }));
    }

    public async makeDefaultAddress(address: Address, wlrIdList: number[]): Promise<number[]> {
        const { DefaultAddressModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(DefaultAddressModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: `${this.modalWindowClass} small-modal`});
        modal.componentInstance.address = address;
        modal.componentInstance.wlsrIdList = wlrIdList;
        return modal.result;
    }

    public async showDecisionPoint(orderDecisionPointData: IDecisionPointData, productDecisionPointData: IDecisionPointData): Promise<number> {
        const { DecisionPointModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(DecisionPointModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.orderDecisionPointData = orderDecisionPointData;
        modal.componentInstance.productDecisionPointData = productDecisionPointData;
        return modal.result.then( dialogResult => {
            if (typeof dialogResult === 'boolean') {
                if (dialogResult === true) {
                    this.ngbModal.dismissAll();
                }
                return null;
            }

            return dialogResult;
        });
    }

    public async showDecisionPointFaq(dpName: string): Promise<boolean> {
        const { DecisionPointFaqComponent } = await this._dialogs;
        const modal = this.ngbModal.open(DecisionPointFaqComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.dpName = dpName;
        return modal.result.then( redirectToFaqPage => {
            if (redirectToFaqPage) {
                this.ngbModal.dismissAll();
                return redirectToFaqPage;
            }
        });
    }


    public async showBudgetDetails(orderItems: OrderItem[]): Promise<void> {
        const { BudgetDetailsModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(BudgetDetailsModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.orderItems = orderItems;
        return modal.result;
    }

    public showPnpBudgetDetails(orderType: 'order' | 'cart',   orderId: number, orderReadonly: boolean): Observable<unknown> {
        return from(this._dialogs.then(({ PnpBudgetDetailsModalComponent }) => {
            const modal = this.ngbModal.open(PnpBudgetDetailsModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
                windowClass: this.modalWindowClass, size: 'xl'});
            modal.componentInstance.budgetType = orderType;
            modal.componentInstance.orderId = orderId;
            modal.componentInstance.orderReadonly = orderReadonly;
            return modal.result.catch( () => {
                return null;
            });
        }));
    }

    public showBudgetApplyDialog(orderId: number): Observable<boolean> {
        return from(this._dialogs.then(({ BudgetApplyModalComponent }) => {
            const modal = this.ngbModal.open(BudgetApplyModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
                windowClass: this.modalWindowClass, size: 'md'});
            modal.componentInstance.orderId = orderId;
            return modal.result.catch( () => {
                return false;
            });
        }));
    }

    public showPnpBudgets(): Promise<void> {
        return firstValueFrom(
            this.store.select(selectHasUserBudgets).pipe(
                take(1),
                switchMap(hasBudgets => {
                    if (hasBudgets) {
                        return of(true);
                    }

                    return this.pnpBudgetService.fetchPnpBudgets().pipe(
                        switchMap(() =>
                            this.store.select(selectHasUserBudgets).pipe(take(1))
                        ),
                        catchError(() => of(false))
                    );
                }),
                switchMap(hasBudgets => {
                    if (!hasBudgets) {
                        return of(undefined);
                    }

                    return from(this._dialogs).pipe(
                        switchMap(({ PnpBudgetModalComponent }) => {
                            const modal = this.ngbModal.open(PnpBudgetModalComponent, {
                                backdrop: 'static',
                                backdropClass: 'backdrop--container',
                                windowClass: this.modalWindowClass,
                                size: 'xl'
                            });

                            return from(modal.result).pipe(
                                map(() => undefined),
                                catchError(() => of(undefined))
                            );
                        })
                    );
                })
            )
        ).then(() => undefined);
    }

    public async showCreditCardPaymentFailed(order: Order, transactionId: string | number): Promise<void> {
        const { CreditCardPaymentFailedComponent } = await this._dialogs;
        const modal = this.ngbModal.open(CreditCardPaymentFailedComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        modal.componentInstance.order = order;
        modal.componentInstance.transactionId = transactionId;
        return modal.result;
    }


    public async showTermsAndConditions(terms: string, withAcceptance = false): Promise<TermsResult> {
        if (!terms) {
            return null;
        }

        const { TermsComponent } = await this._dialogs;
        const modal = this.ngbModal.open(TermsComponent,
            {keyboard: false, backdrop: true, size: 'xl',
                backdropClass: 'backdrop--container', windowClass: this.modalWindowClass});
        modal.componentInstance.content = terms;
        modal.componentInstance.withAcceptance = withAcceptance;
        return modal.result;
    }

    public showLatestNews(news: string, size = 'xl'): Observable<void> {
        if (!news) {
            return of(null);
        }
        return from(this._dialogs.then(({ NewsModalComponent }) => {
            const modal = this.ngbModal.open(NewsModalComponent,
                {keyboard: true, backdrop: true, size,
                    backdropClass: 'backdrop--container',
                    windowClass: 'modal-window--container'});
            modal.componentInstance.content = news;
            return modal.result.catch( () => {
                return null;
            });
        }));
    }


    public async showLogout(): Promise<boolean> {
        const { LogoutModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(LogoutModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass});
        return modal.result.catch( () => {
            return null;
        });
    }

    public async showRecommendedProductExportResult(exportType: 'pdf' | 'xlsx'): Promise<void> {
        const { ExportResultModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(ExportResultModalComponent, {backdrop: 'static',
            backdropClass: 'backdrop--container',
            windowClass: `${this.modalWindowClass}  export--modal`});
        modal.componentInstance.exportType = exportType;
        return modal.result.catch( () => {
            return null;
        });
    }

    public async showManageOrderListAddressesModal(orderType: string, shouldUpdateShipments = false): Promise<Order> {
        const { ManageOrderListAddressesComponent } = await this._dialogs;
        const modal = this.ngbModal.open(ManageOrderListAddressesComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass, });
        modal.componentInstance.orderType = orderType;
        modal.componentInstance.shouldUpdateShipments =  shouldUpdateShipments;
        return modal.result;
    }


    public showEditContactDetailsModal({order, orderItem,  orderItemDelivery}:
        {order: Order, orderItem: OrderItem, orderItemDelivery: OrderItemDelivery}): Observable<EditContactDetailsResult> {
        return from(this._dialogs.then(({ EditContactDetailsModalComponent }) => {
            const modal = this.ngbModal.open(EditContactDetailsModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
                windowClass: this.modalWindowClass, });
            modal.componentInstance.order = order;
            modal.componentInstance.orderItem = orderItem;
            modal.componentInstance.orderItemDelivery = orderItemDelivery;
            return modal.result;
        }));
    }

    public async showContactDetailsInfoModal(address: Address) {
        if (!address || (!address.phone && !address.contact_details?.length)) {
            return;
        }
        const { ContactDetailsInfoModalComponent } = await this._dialogs;
        const modal = this.ngbModal.open(ContactDetailsInfoModalComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
            windowClass: this.modalWindowClass, });
        modal.componentInstance.address = address;
        return modal.result;
    }

    public showDistributionGroupModal(): Observable<DistributionGroup | undefined> {
        return from(this._dialogs.then(({ DistributionGroupDialogComponent }) => {
            const modal = this.ngbModal.open(DistributionGroupDialogComponent, {backdrop: 'static', backdropClass: 'backdrop--container',
                windowClass: this.modalWindowClass, });
            return modal.result;
        }));
    }

    private get modalWindowClass(): string {
        if (this.mobileCheck.mobileDevice) {
            return 'mobile';
        } else if (this.mobileCheck.tabletDevice) {
            return 'tablet';
        }

        return '';

    }
}
